import { isCancelledError, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useCallback, useRef } from "react";

import { queryKeys } from "@/queryKeys";
import { handleApiError, uploadErrorText } from "@/services/api";

import {
  createThreadWorkspace,
  getPanelFiles,
  getThreadWorkspaces,
  getWorkspaceFileBytes,
  uploadWorkspaceFile,
  type PanelFiles,
  type SandboxWorkspaceEntry,
  type UploadedFile,
  type WorkspaceFile,
  type WorkspaceUsage,
} from "../sandbox/client/api";
import { downloadBytes } from "./files";
import { uploadRefusal } from "./workspaceRoom";

const NO_FILES: WorkspaceFile[] = [];

/** An upload refused before it is sent (no room for it): its message is the one to show. */
class UploadRefused extends Error {}

/** A Files panel file's name: its path below publish/ or uploads/. */
export const panelName = (path: string) => path.slice(path.indexOf("/") + 1);

const FOLDER_LABELS: Record<string, string> = { publish: "Published", uploads: "Uploaded" };

/** Where a Files panel file came from, by its folder. */
export const folderLabel = (file: WorkspaceFile) => FOLDER_LABELS[file.class] ?? file.class;

/**
 * The workspace the thread's calls run in: the one the server marks `current`. Never by label: a
 * sandbox the agent opens with new_sandbox takes over from "default". An older server marks none,
 * so then the newest (the list comes oldest first, and its current_workspace takes the newest);
 * a list whose entries are all marked false has none.
 */
const currentWorkspace = (workspaces: SandboxWorkspaceEntry[]) =>
  workspaces.some((workspace) => workspace.current !== undefined)
    ? (workspaces.find((workspace) => workspace.current) ?? null)
    : (workspaces.at(-1) ?? null);

/**
 * `list` with `made` in it. One just made goes in as the current workspace (POST's reply carries
 * no `current`); one already listed keeps the list's flags, which may be newer.
 */
const withCurrent = (list: SandboxWorkspaceEntry[], made: SandboxWorkspaceEntry) =>
  list.some((w) => w.id === made.id)
    ? list
    : [...list.map((w) => ({ ...w, current: false })), { ...made, current: true }];

const usageOf = ({ size_bytes, max_bytes, free_bytes, used_share }: WorkspaceUsage) => ({
  size_bytes,
  max_bytes,
  free_bytes,
  used_share,
});

export interface WorkspaceFiles {
  /** The thread's current workspace (on its context when the thread is bound), or null before one. */
  workspace: SandboxWorkspaceEntry | null;
  /** publish/ then uploads/, as the API sorts them. */
  files: WorkspaceFile[];
  usage: WorkspaceUsage | null;
  /** The listing of a workspace with files: loading until its first answer, failed if that failed. */
  listing: "loading" | "failed" | "ready";
  /** Asks for the listing again. */
  retry: () => void;
}

export interface ThreadWorkspace extends WorkspaceFiles {
  /** Puts `file` in uploads/ (making the workspace first if needed); rejects with what to show. */
  upload: (file: File) => Promise<UploadedFile>;
  /** The file's bytes, for a preview. */
  read: (file: WorkspaceFile) => Promise<ArrayBuffer>;
  /** Saves the file through a download, never rendering it. */
  download: (file: WorkspaceFile) => Promise<void>;
}

/**
 * A thread's workspace and its Files panel files, to read only (the @ list's workspace files).
 * Refetched after an upload here and on the socket's `workspace.changed` (contexts/invalidation).
 */
export function useWorkspaceFiles(threadId: number | null): WorkspaceFiles {
  const { data: workspace = null } = useQuery({
    queryKey: queryKeys.agentWorkspaces(threadId ?? 0),
    queryFn: () => getThreadWorkspaces(threadId as number),
    select: currentWorkspace,
    enabled: threadId != null,
  });
  const workspaceId = workspace?.id ?? null;
  const count = workspace?.files ?? 0;
  // A workspace with no files at all has none to list: no request, and an earlier listing (kept in
  // the cache while the query is off) is not shown.
  const {
    data: listing,
    isError,
    refetch,
  } = useQuery({
    queryKey: queryKeys.agentWorkspaceFiles(workspaceId ?? ""),
    queryFn: () => getPanelFiles(workspaceId as string),
    enabled: workspaceId != null && count > 0,
  });
  const retry = useCallback(() => void refetch(), [refetch]);
  // The listing's usage is the newer when both are there.
  const usage: WorkspaceUsage | null = count > 0 ? (listing ?? workspace) : workspace;
  return {
    workspace,
    files: count > 0 ? (listing?.files ?? NO_FILES) : NO_FILES,
    usage,
    listing: count > 0 && !listing ? (isError ? "failed" : "loading") : "ready",
    retry,
  };
}

/** The Files panel's data for a thread (`useWorkspaceFiles`), and what it does with them. */
export function useThreadWorkspace(threadId: number | null): ThreadWorkspace {
  const queryClient = useQueryClient();
  const files = useWorkspaceFiles(threadId);
  const workspaceId = files.workspace?.id ?? null;
  // The thread's workspace as it is being made, so uploads at once make it once.
  const creating = useRef(new Map<number, Promise<SandboxWorkspaceEntry>>());

  const upload = useCallback(
    async (file: File) => {
      if (threadId == null) throw new Error("Open a conversation first.");
      const workspacesKey = queryKeys.agentWorkspaces(threadId);
      // Asked of the server, not the cache, and once: a failed list is shown, not retried. A
      // sandbox call that ends meanwhile cancels the fetch (contexts/invalidation): its answer is
      // then the list from before (or a CancelledError), and the list is marked, so it asks again.
      const fetchWorkspaces = async (): Promise<SandboxWorkspaceEntry[]> => {
        for (;;) {
          try {
            const list = await queryClient.fetchQuery({
              queryKey: workspacesKey,
              queryFn: () => getThreadWorkspaces(threadId),
              staleTime: 0,
              retry: false,
            });
            if (!queryClient.getQueryState(workspacesKey)?.isInvalidated) return list;
          } catch (error) {
            if (!isCancelledError(error)) throw new Error(handleApiError(error as AxiosError));
          }
        }
      };
      // An answer into the cache. Writing it clears a mark the socket set meanwhile, so the mark is
      // set again: the next upload still asks.
      const updateList = (update: (old: SandboxWorkspaceEntry[]) => SandboxWorkspaceEntry[]) => {
        const marked = queryClient.getQueryState(workspacesKey)?.isInvalidated;
        queryClient.setQueryData<SandboxWorkspaceEntry[]>(workspacesKey, (old = []) => update(old));
        if (marked) {
          void queryClient.invalidateQueries({ queryKey: workspacesKey, refetchType: "none" });
        }
      };
      // From the cache, not this render: in a loop of uploads, the room the last one left. Asked
      // for when there is none yet, or when the socket marked it changed (a sandbox call ended:
      // contexts/invalidation), so the workspace the agent opened is the one used. The refetch
      // after an upload (finally) marks nothing, so the next file in a loop still reads the cache.
      const cached = queryClient.getQueryState<SandboxWorkspaceEntry[]>(workspacesKey);
      const cachedList = cached && !cached.isInvalidated ? cached.data : undefined;
      const entry = currentWorkspace(cachedList ?? (await fetchWorkspaces()));
      const panel =
        entry && entry.files > 0
          ? queryClient.getQueryData<PanelFiles>(queryKeys.agentWorkspaceFiles(entry.id))
          : undefined;
      const refusal = uploadRefusal(file, panel ?? entry);
      if (refusal) throw new Error(refusal);
      let target = entry?.id ?? null;
      try {
        let created: SandboxWorkspaceEntry | null = null;
        if (!target) {
          let made = creating.current.get(threadId);
          if (!made) {
            // A cached empty list is asked again first: the agent may have opened one
            // (new_sandbox) since, and a "default" made now would take over from it. In the cache
            // as soon as it exists, so the next call finds it once this promise is gone.
            made = (
              cachedList ? fetchWorkspaces() : Promise.resolve<SandboxWorkspaceEntry[]>([])
            ).then(
              (latest) =>
                currentWorkspace(latest) ??
                createThreadWorkspace(threadId).then((fresh) => {
                  updateList((old) => withCurrent(old, fresh));
                  return fresh;
                })
            );
            creating.current.set(threadId, made);
            const settled = () => creating.current.delete(threadId);
            made.then(settled, settled);
          }
          created = await made;
          // One the agent opened (above) has its own room.
          const foundRefusal = uploadRefusal(file, created);
          if (foundRefusal) throw new UploadRefused(foundRefusal);
          target = created.id;
        }
        const uploaded = await uploadWorkspaceFile(target, file);
        const id = target;
        const usageNow = usageOf(uploaded);
        // The answer's usage (and one more file) in the cache, for the next upload's check.
        updateList((old) => {
          const list = created ? withCurrent(old, created) : old;
          return list.map((w) => (w.id === id ? { ...w, ...usageNow, files: w.files + 1 } : w));
        });
        queryClient.setQueryData<PanelFiles>(
          queryKeys.agentWorkspaceFiles(id),
          (old) => old && { ...old, ...usageNow }
        );
        return uploaded;
      } catch (error) {
        if (error instanceof UploadRefused) throw error;
        throw new Error(uploadErrorText(error, file.name));
      } finally {
        // A refetch, not an invalidation: the list stays unmarked for the next upload (above).
        void queryClient.refetchQueries({ queryKey: workspacesKey });
        if (target) {
          void queryClient.invalidateQueries({ queryKey: queryKeys.agentWorkspaceFiles(target) });
        }
      }
    },
    [queryClient, threadId]
  );

  const read = useCallback(
    async (file: WorkspaceFile) => {
      if (!workspaceId) throw new Error("No workspace yet.");
      return getWorkspaceFileBytes(workspaceId, file.path);
    },
    [workspaceId]
  );

  const download = useCallback(
    async (file: WorkspaceFile) =>
      downloadBytes(await read(file), file.path.split("/").pop() ?? ""),
    [read]
  );

  return { ...files, upload, read, download };
}
